"use client"

import AccessDenied from "@/components/custom-access-denied"
import { YOUTUBE_VIDEO } from "@/constants/admin-paths"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { useYouTubeVideo } from "@/hook/useYouTubeVideo"
import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import { VideoAttributes } from "@/types/types"
import { createPath } from "@/utils/create-path"
import { FormSection, FormShell, FormSkeleton, StickyActionBar } from "@/(admin)/components/form"
import { CheckCircleOutlined, PlusCircleOutlined } from "@ant-design/icons"
import { Button, Form } from "antd"
import { useSearchParams } from "next/navigation"
import React, { useCallback, useEffect, useState } from "react"
import YouTubeVideoFormFields from "../components/YouTubeVideoFormFields"

const FormUpsertYouTubeVideo: React.FC = () => {
  const [form] = Form.useForm()
  const searchParams = useSearchParams()
  const id = searchParams.get("id")

  const isEditMode = Boolean(id)
  const { loading, fetchVideoById, createVideo, updateVideo, hasPermission, handleBack } = useYouTubeVideo()
  const { hasPermission: checkPermission } = useCheckPermission()
  const [isFormReady, setIsFormReady] = useState(false)
  const getNormalizedSnapshot = useCallback(() => {
    const values = form.getFieldsValue(true)
    return {
      title: values.title || "",
      url: values.url || "",
      videoId: values.videoId || "",
      channelId: values.channelId || "",
      channelTitle: values.channelTitle || "",
      playlistId: values.playlistId || "",
      description: values.description || "",
    }
  }, [form])

  const { isChange, checkChange, commitSnapshot, modal, message, handleCancel: guardedHandleCancel } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot,
  })

  const loadVideo = useCallback(async () => {
    if (!id) return
    const videoData = await fetchVideoById(Number(id))
    if (videoData) {
      form.setFieldsValue({
        title: videoData.attributes.title,
        url: videoData.attributes.url || "",
        videoId: videoData.attributes.videoId || `youtube-video-${videoData.id}`,
        channelId: videoData.attributes.channelId || "",
        channelTitle: videoData.attributes.channelTitle || "",
        playlistId: videoData.attributes.playlistId || "",
        description: videoData.attributes.description || "",
      })
      setIsFormReady(true);
    }
  }, [id, fetchVideoById, form])

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot();
      });
      return () => cancelAnimationFrame(animationId);
    }
  }, [isFormReady, commitSnapshot]);

  const handleSubmit = async () => {
    const values = await form.validateFields()
    const now = new Date().toISOString()

    const payload: Partial<VideoAttributes> = {
      title: values.title,
      url: values.url,
      youtubeUrl: values.url,
      videoId: values.videoId || `youtube-video-${Date.now()}`,
      videoPublishedAt: now,
      syncedAt: now,
      playlistId: values.playlistId?.trim() || "",
    }

    if (values.channelId?.trim()) payload.channelId = values.channelId.trim()
    if (values.channelTitle?.trim()) payload.channelTitle = values.channelTitle.trim()
    if (values.description?.trim()) payload.description = values.description.trim()

    if (isEditMode && id) {
      await updateVideo(id, payload)
    } else {
      await createVideo(payload)
    }
    commitSnapshot()
  }

  useEffect(() => {
    if (isEditMode && id) {
      loadVideo()
    } else {
      form.setFieldsValue({ videoId: `youtube-video-${Date.now()}` })
      setIsFormReady(true);
    }
  }, [id, isEditMode, loadVideo, form])

  const handleCancelClick = () => {
    const targetPath = createPath(YOUTUBE_VIDEO.LIST.PATH, searchParams)
    guardedHandleCancel(targetPath)
  }

  if (!hasPermission(isEditMode ? "update" : "create")) {
    return <AccessDenied />
  }

  if (isEditMode && !isFormReady) {
    return <FormSkeleton mainSections={2} sidebarCards={0} />
  }

  const crumbs = [
    { title: t("YouTube Video"), href: createPath(YOUTUBE_VIDEO.LIST.PATH, searchParams) },
    { title: isEditMode ? t("Chỉnh sửa") : t("Tạo mới") },
  ]

  const main = (
    <FormSection title={t("Cơ bản")} collapsible={false}>
      <YouTubeVideoFormFields form={form} isEditMode={isEditMode} />
    </FormSection>
  )

  const actionBar = (
    <StickyActionBar
      right={
        <>
          <Button danger onClick={handleCancelClick}>
            {t("Cancel")}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={isEditMode ? <CheckCircleOutlined /> : <PlusCircleOutlined />}
          >
            {isEditMode ? t("Cập nhật Video") : t("Add New Video")}
          </Button>
        </>
      }
    />
  )

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={checkChange}>
      <FormShell
        title={isEditMode ? t("Edit YouTube Video") : t("Add New YouTube Video")}
        crumbs={crumbs}
        main={main}
        actionBar={actionBar}
      />
    </Form>
  )
}

export default FormUpsertYouTubeVideo